/**
 * @fileoverview Source citations — parsing (MOR-394)
 *
 * Split out of `sourceCitations.tsx` (react-refresh/only-export-components)
 * — pure parsing of the `morgan-nav` sources protocol out of assistant text.
 * See `sourceCitations.tsx` for the `<SourceChips />` renderer.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CitedSource {
  kind: string;
  id: string;
  /** Owning job id — required for deep-linking workspace-scope hits. */
  jobId?: string;
  orgId?: string;
  /** Short label shown on the chip. Falls back to kind+id when missing. */
  title?: string;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Matches ` ```morgan-nav … ``` ` blocks. The opening fence may be
 * followed by an optional newline; the JSON payload runs until the
 * next closing triple-backtick. Lazy to tolerate multiple blocks
 * (we only keep the first / longest).
 */
const MORGAN_NAV_FENCE_RE = /```morgan-nav\s*\n?([\s\S]*?)```/g;

/**
 * Legacy fallback — a bare JSON array tacked onto the END of the
 * reply, with no fence. Older agent replies (and the historical
 * threads the user already has) use this shape. We only accept it
 * when it looks like an array of {kind,id,...} objects and is
 * positioned at the very end of the message.
 */
const TRAILING_BARE_JSON_RE = /\n*\[\s*\{[\s\S]+?\}\s*\]\s*$/;

function isSourceArray(value: unknown): value is CitedSource[] {
  if (!Array.isArray(value)) return false;
  if (value.length === 0) return false;
  return value.every(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      typeof (entry as { kind?: unknown }).kind === "string" &&
      typeof (entry as { id?: unknown }).id === "string",
  );
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Splits an assistant message into the human-facing prose and the
 * parsed source citations. The prose is what we hand to the markdown
 * renderer; the sources become chips.
 *
 * Order of preference:
 * 1. A `morgan-nav` fenced code block (current contract).
 * 2. A bare trailing JSON array (legacy — kept so existing threads
 *    don't render giant JSON dumps until they age out).
 */
export function extractCitedSources(content: string): {
  stripped: string;
  sources: CitedSource[];
} {
  if (!content) return { stripped: content, sources: [] };

  // 1. Fenced `morgan-nav` block.
  let stripped = content;
  let sources: CitedSource[] = [];
  let matched = false;
  stripped = stripped.replace(MORGAN_NAV_FENCE_RE, (_full, inner: string) => {
    matched = true;
    const parsed = safeParseJson(inner.trim());
    if (isSourceArray(parsed)) {
      // Merge — multiple blocks shouldn't happen but de-dup just in case.
      const seen = new Set(sources.map((s) => `${s.kind}::${s.id}`));
      for (const s of parsed) {
        const key = `${s.kind}::${s.id}`;
        if (!seen.has(key)) {
          sources.push(s);
          seen.add(key);
        }
      }
    }
    return "";
  });
  if (matched) {
    return { stripped: stripped.trimEnd(), sources };
  }

  // 2. Legacy bare trailing JSON array.
  const tailMatch = stripped.match(TRAILING_BARE_JSON_RE);
  if (tailMatch) {
    const parsed = safeParseJson(tailMatch[0].trim());
    if (isSourceArray(parsed)) {
      sources = parsed;
      stripped = stripped.slice(0, tailMatch.index).trimEnd();
    }
  }

  return { stripped, sources };
}

// ---------------------------------------------------------------------------
// Inline parsing — splits content into interleaved prose + source segments
// ---------------------------------------------------------------------------

export type ContentSegment =
  { type: "prose"; text: string } | { type: "sources"; sources: CitedSource[] };

/**
 * Splits assistant content into alternating prose and inline source
 * segments, preserving the document order. Each `morgan-nav` fence
 * becomes a `sources` segment; everything between fences becomes a
 * `prose` segment. Legacy messages (bare trailing JSON or a single
 * block at the end) also work — they produce one prose segment + one
 * trailing sources segment.
 */
export function extractInlineSources(content: string): ContentSegment[] {
  if (!content) return [{ type: "prose", text: "" }];

  const segments: ContentSegment[] = [];
  const fenceRe = /```morgan-nav\s*\n?([\s\S]*?)```/g;

  const lastIndex = Array.from(content.matchAll(fenceRe)).reduce(
    (index, match) => {
      const matchIndex = match.index ?? 0;
      // Prose before this fence
      const proseText = content.slice(index, matchIndex);
      if (proseText.trim()) {
        segments.push({ type: "prose", text: proseText.trimEnd() });
      }

      // Parse the sources from this fence
      const parsed = safeParseJson(match[1].trim());
      if (isSourceArray(parsed)) {
        segments.push({ type: "sources", sources: parsed });
      }

      return matchIndex + match[0].length;
    },
    0,
  );

  // Trailing content after the last fence
  const tail = content.slice(lastIndex);

  if (segments.length === 0) {
    // No fenced blocks found — try legacy bare trailing JSON
    const tailMatch = tail.match(TRAILING_BARE_JSON_RE);
    if (tailMatch) {
      const parsed = safeParseJson(tailMatch[0].trim());
      if (isSourceArray(parsed)) {
        const proseText = tail.slice(0, tailMatch.index).trimEnd();
        if (proseText) segments.push({ type: "prose", text: proseText });
        segments.push({ type: "sources", sources: parsed });
        return segments;
      }
    }
    // No sources at all
    return [{ type: "prose", text: content }];
  }

  // Append any trailing prose after the last fence
  if (tail.trim()) {
    segments.push({ type: "prose", text: tail.trimStart() });
  }

  return segments;
}
